import React, { useState, useRef, useEffect } from "react";
import { projectsData } from "../data/projects";

const Projects = () => {
  const [activeId, setActiveId]   = useState(null);
  const [pulseId,  setPulseId]    = useState(null);
  const panelRef                  = useRef(null);

  const handleCardClick = (id) => {
    if (id === activeId) {
      setActiveId(null);
      return;
    }
    setActiveId(id);
    setPulseId(id);
    setTimeout(() => setPulseId(null), 600);
  };

  // Scroll to panel when it opens — works on mobile and desktop
  useEffect(() => {
    if (activeId !== null && panelRef.current) {
      setTimeout(() => {
        const panel = panelRef.current;
        const top = panel.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: "smooth" });
      }, 80);
    }
  }, [activeId]);

  const handleResumeDownload = () => {
    const project = projectsData.find((p) => p.id === activeId);
    if (project?.resume) {
      const link = document.createElement("a");
      link.href = "/Farha_Lina_Resume.pdf";
      link.download = "Farha_Lina_Resume.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const activeProject = projectsData.find((p) => p.id === activeId) || null;

  return (
    <section id="projects">
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <p className="section-eyebrow">My work</p>
        <h2 className="projects-title">What I've built &amp; where I've been</h2>

        <div className="projects-grid">
          {projectsData.map((project) => {
            const isActive = activeId === project.id;
            const isPulse  = pulseId  === project.id;
            return (
              <div
                key={project.id}
                className={`exp-card ${isActive ? "active" : ""} ${project.featured ? "featured" : ""} ${isPulse ? "pulse" : ""}`}
                onClick={() => handleCardClick(project.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleCardClick(project.id)}
                aria-expanded={isActive}
              >
                {project.featured && (
                  <span className="exp-card-featured-pill">✦ Building now</span>
                )}

                <div className="exp-card-header">
                  <h3 className="exp-card-company">{project.title}</h3>
                  {project.badge && (
                    <span className="exp-card-badge">{project.badge}</span>
                  )}
                </div>

                {project.role && (
                  <p className="exp-card-role">{project.role}</p>
                )}

                <p className="exp-card-desc">{project.description}</p>

                {project.technologies && (
                  <div className="exp-card-tags">
                    {project.technologies.slice(0, 4).map((t) => (
                      <span key={t} className="exp-tag">{t}</span>
                    ))}
                    {project.technologies.length > 4 && (
                      <span className="exp-tag">+{project.technologies.length - 4}</span>
                    )}
                  </div>
                )}

                {/* Tap hint on mobile */}
                <p className="exp-card-tap-hint">
                  {isActive ? "↑ tap to close" : "tap to learn more ↓"}
                </p>
              </div>
            );
          })}

          {/* Detail panel */}
          {activeProject && (
            <div className="exp-detail-panel" ref={panelRef}>
              <div className="panel-left">
                <h3 className="panel-title">{activeProject.title}</h3>

                {activeProject.role && (
                  <div className="panel-role-row">
                    <span className="panel-role">{activeProject.role}</span>
                    {activeProject.duration && (
                      <span className="panel-duration">{activeProject.duration}</span>
                    )}
                  </div>
                )}

                <p className="panel-detail-text">{activeProject.details}</p>

                {activeProject.impact && (
                  <p className="panel-detail-text" style={{ color: "var(--green-pale)", fontStyle: "italic" }}>
                    ✦ {activeProject.impact}
                  </p>
                )}

                {activeProject.learningPath && (
                  <>
                    <p className="panel-subtitle">Education</p>
                    <p className="panel-detail-text">
                      {activeProject.learningPath.education} · {activeProject.learningPath.location}
                    </p>
                    <p className="panel-detail-text">{activeProject.learningPath.continuation}</p>
                  </>
                )}

                {activeProject.languages && (
                  <>
                    <p className="panel-subtitle" style={{ marginTop: "1rem" }}>Languages I speak</p>
                    <div className="panel-tech-tags">
                      {activeProject.languages.map((l) => (
                        <span key={l} className="panel-tech-tag">{l}</span>
                      ))}
                    </div>
                  </>
                )}

                {activeProject.link && (
                  <a
                    href={activeProject.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="panel-download-btn"
                    style={{ marginTop: "1.5rem", textDecoration: "none" }}
                  >
                    View Chromaly →
                  </a>
                )}

                {activeProject.resume && (
                  <button className="panel-download-btn" onClick={handleResumeDownload}>
                    ↓ Download Resume
                  </button>
                )}
              </div>

              <div className="panel-right">
                {activeProject.achievements && (
                  <>
                    <p className="panel-subtitle">Key highlights</p>
                    <ul className="panel-achievements">
                      {activeProject.achievements.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </>
                )}

                {activeProject.technologies && (
                  <>
                    <p className="panel-subtitle" style={{ marginTop: "1.5rem" }}>Technologies</p>
                    <div className="panel-tech-tags">
                      {activeProject.technologies.map((t) => (
                        <span key={t} className="panel-tech-tag">{t}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Projects;
